/**
 * Special context rendering utilities.
 *
 * Handles detection and processing of special contexts that require
 * custom rendering (e.g., two-column layout for comparison).
 *
 * @module specialContextUtils
 */

import type { ExamType, CEFRLevel } from '@/types/exam-simulation';

/**
 * Check if a context requires special two-column rendering
 *
 * @param examType - Exam type (e.g., "goethe", "osd", "telc")
 * @param level - CEFR level (e.g., "A1", "B2")
 * @param sectionId - Section number
 * @param content - Context content to check for separator
 * @returns true if context should be rendered in two columns
 */
export function requiresTwoColumnRendering(
  examType: ExamType,
  level: CEFRLevel,
  sectionId: number,
  content: string,
): boolean {
  // Special case: Goethe A1 Reading Section 2 with "------" separator
  if (
    examType === 'goethe' &&
    level === 'A1' &&
    sectionId === 2 &&
    content.includes('------')
  ) {
    return true;
  }

  return false;
}

/**
 * Split context content by "------" separator into left and right parts
 *
 * Removes the separator completely from both parts.
 *
 * @param content - Context content containing "------" separator
 * @returns Object with left and right content parts (separator removed)
 */
export function splitContextBySeparator(content: string): {
  left: string;
  right: string;
} {
  // Split by "------" separator
  const parts = content.split('------');

  if (parts.length < 2) {
    // If no separator found, return empty right part
    return {
      left: content.trim(),
      right: '',
    };
  }

  // Take first part as left, join remaining parts as right
  // The separator is already removed by split, just join the parts
  const left = parts[0].trim();
  const right = parts
    .slice(1)
    .join('') // Join without separator (separator already removed by split)
    .trim();

  return { left, right };
}

